// ------------------------------------------------------------------------------
// name: ChunkUploadManager
// author: mudas
// created: 2024-10-14
// ------------------------------------------------------------------------------

import { RcFile } from 'antd/es/upload/interface';
import { ChunkOption } from './types';
import { FileUploader } from './FileUploader';

export interface UploadChangeDetail {
  total: number;
  completed: number;
  failed: number;
}

export interface UploadManagerOptions {
  // 是否在添加文件自动开始上传（否则需要手动调用 upload() 方法调度上传）
  autoUpload?: boolean;

  // 全部文件上传完成（包含成功和失败）
  onCompleted?: (hasError?: boolean) => void;

  // 文件上传结果变化（单个文件的上传成功和失败都会触发）
  onChange?: (value: UploadChangeDetail) => void;

  // 总进度变化
  onPercent?: (value: number) => void;
}

export class UploadManager {
  protected loaders: FileUploader[] = [];

  constructor(private options?: UploadManagerOptions) {}

  // --------------------------------------------------------------------------
  //
  // Class properties
  //
  // --------------------------------------------------------------------------

  get autoUpload() {
    return this.options?.autoUpload ?? true;
  }

  get total() {
    return this.loaders.length;
  }

  protected _completed: number = 0;

  get completed() {
    return this._completed;
  }

  protected _failed: number = 0;

  get failed() {
    return this._failed;
  }

  // --------------------------------------------------------------------------
  //
  // Class methods
  //
  // --------------------------------------------------------------------------

  // 根据文件上传完成度，计算和反馈总进度
  // 调度级别：文件

  /**
   * 增量更新配置
   * @param options
   * @param replace 是否以当前配置完全覆盖已有配置（默认：false 不覆盖）
   */
  setOptions(options: Partial<UploadManagerOptions>, replace = false) {
    this.options = replace ? options : { ...this.options, ...options };
  }

  has(file: RcFile) {
    return this.loaders.some((item) => item.uid === file.uid);
  }

  get(uid: string) {
    return this.loaders.find((item) => item.uid === uid);
  }

  getBatch(uids?: string[]) {
    return uids?.length
      ? uids.reduce((loaders, uid) => {
          const loader = this.get(uid);
          loader && loaders.push(loader);
          return loaders;
        }, [] as FileUploader[])
      : this.loaders;
  }

  add(file: RcFile, options?: ChunkOption) {
    if (!this.has(file)) this.loaders.push(new FileUploader(file, options));
    if (this.autoUpload) this.upload(file.uid, options);
    this.handleChange();
  }

  getPencent() {
    return this.loaders.reduce((total, loader) => total + loader.percent, 0) / this.loaders.length;
  }

  reset() {
    this.loaders = [];
    this._completed = 0;
    this._failed = 0;
  }

  /**
   * 开始上传指定文件
   * @param uid 需要开始上传的目标文件编号
   * @param options 上传配置
   */
  upload(uid: string, options?: ChunkOption) {
    const loader = this.get(uid);
    loader?.upload(options);
  }

  /**
   * 开始上传所有文件
   * @param options 上传配置
   */
  uploadAll(
    options?: Omit<ChunkOption, 'onChunkInit' | 'onChunkFailed' | 'onChunkCompleted'>,
    sequential: boolean = false,
    batchSize: number = 5,
  ) {
    const { onFinished, onFailed, onProgress, ...other } = options || {};
    // 获取待上传的文件加载器
    const pendingLoaders = this.loaders.filter((loader) => !loader.loading && !loader.completed);

    if (pendingLoaders.length === 0) {
      return;
    }

    if (sequential) {
      // 顺序上传实现
      this.uploadSequentially(pendingLoaders, { onFinished, onFailed, onProgress, ...other });
    } else {
      // 批量并行上传实现
      this.uploadInBatches(pendingLoaders, { onFinished, onFailed, onProgress, ...other }, batchSize);
    }
  }

  /**
   * 顺序上传实现
   * @private
   */
  private uploadSequentially(
    loaders: FileUploader[],
    options: Omit<ChunkOption, 'onChunkInit' | 'onChunkFailed' | 'onChunkCompleted'>,
  ) {
    const { onFinished, onFailed, onProgress, ...other } = options || {};
    let currentIndex = 0;

    const uploadNext = () => {
      if (currentIndex < loaders.length) {
        const loader = loaders[currentIndex];
        currentIndex++;

        loader.upload({
          onFinished: (url, file) => {
            this._completed++;
            this.handleChange();
            onFinished?.(url, file);
            // 当前文件上传完成后，开始上传下一个
            uploadNext();
          },
          onFailed: (error) => {
            this._failed++;
            this.handleChange();
            onFailed?.(error);
            // 即使当前文件上传失败，也继续上传下一个
            uploadNext();
          },
          onProgress: (percent, file) => {
            onProgress?.(percent, file);
            this.options?.onPercent?.(this.getPencent());
          },
          ...other,
        });
      }
    };

    // 开始上传第一个文件
    uploadNext();
  }

  /**
   * 批量并行上传实现
   * @private
   */
  private uploadInBatches(
    loaders: FileUploader[],
    options: Omit<ChunkOption, 'onChunkInit' | 'onChunkFailed' | 'onChunkCompleted'>,
    batchSize: number,
  ) {
    const { onFinished, onFailed, onProgress, ...other } = options || {};

    // 跟踪当前批次完成的上传数量
    let completedInBatch = 0;

    // 按批次处理
    const processBatch = (startIndex: number) => {
      if (startIndex >= loaders.length) {
        // 所有批次已处理完成
        return;
      }

      // 获取当前批次的加载器
      const currentBatch = loaders.slice(startIndex, startIndex + batchSize);
      completedInBatch = 0;

      // 为每个加载器创建上传回调
      currentBatch.forEach((loader) => {
        loader.upload({
          onFinished: (url, file) => {
            this._completed++;
            this.handleChange();
            onFinished?.(url, file);

            // 记录当前批次完成数量
            completedInBatch++;

            // 当前批次全部完成时，处理下一批次
            if (completedInBatch === currentBatch.length) {
              processBatch(startIndex + batchSize);
            }
          },
          onFailed: (error) => {
            this._failed++;
            this.handleChange();
            onFailed?.(error);

            // 记录当前批次完成数量（失败也算完成）
            completedInBatch++;

            // 当前批次全部完成时，处理下一批次
            if (completedInBatch === currentBatch.length) {
              processBatch(startIndex + batchSize);
            }
          },
          onProgress: (percent, file) => {
            onProgress?.(percent, file);
            this.options?.onPercent?.(this.getPencent());
          },
          ...other,
        });
      });
    };

    // 开始处理第一批
    processBatch(0);
  }
  /**
   * 停止上传
   * @param uids 若不指定，则停止所有文件的上传
   */
  stop(uids?: string[]) {
    const target = this.getBatch(uids ?? []);
    target.forEach((loader) => loader.stop());
    return target;
  }

  /**
   * 恢复上传
   * @param uids 若不指定，则尝试恢复所有已暂停上传的文件（进行中的上传不受影响）
   */
  resume(uids?: string[]) {
    const target = this.getBatch(uids ?? []);
    target.forEach((loader) => loader.resume());
    return target;
  }

  /**
   * 停止并移除正在上传的文件
   * @param uids 若不指定，则停止并移除所有文件的上传
   */
  remove(uids?: string[]) {
    const target = this.stop(uids);
    this.loaders = this.loaders.filter((loader) => !target.some((item) => item.uid === loader.uid));

    // 移除目标后，若仍有剩余，更新完成和失败数量
    if (this.loaders.length) {
      this._completed = this.loaders.filter((loader) => loader.completed).length;
      this._failed = this.loaders.length - this._completed;
    }

    return target;
  }

  handleChange() {
    const result: UploadChangeDetail = { total: this.total, completed: this._completed, failed: this._failed };
    this.options?.onChange?.(result);
    if (result.completed + result.failed === result.total) this.options?.onCompleted?.(result.failed > 0);
  }
}
